import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Clock, Plus, Calendar, ArrowRight, Trash2 } from 'lucide-react';

const CAT_COLORS = {
  contractual: 'bg-blue-100 text-blue-700 border-blue-300',
  factual: 'bg-slate-100 text-slate-700 border-slate-300',
  procedural: 'bg-purple-100 text-purple-700 border-purple-300',
  correspondence: 'bg-amber-100 text-amber-700 border-amber-300',
  financial: 'bg-rose-100 text-rose-700 border-rose-300',
  delay: 'bg-red-100 text-red-700 border-red-300',
};
const SIG_COLORS = { low: 'bg-slate-100 text-slate-600', medium: 'bg-blue-100 text-blue-700', high: 'bg-amber-100 text-amber-700', critical: 'bg-red-600 text-white' };
const CATEGORIES = ['contractual', 'factual', 'procedural', 'correspondence', 'financial', 'delay'];

export default function ChronologyBuilder({ matterId, issues, evidence }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [form, setForm] = useState({
    date: '', end_date: '', title: '', description: '',
    category: 'factual', significance: 'medium',
    linked_evidence_ids: [], linked_issue_ids: [],
  });

  const loadData = useCallback(async () => {
    try {
      const res = await api.disputes.listChronology(matterId);
      setEntries(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!form.title.trim() || !form.date) { toast.error('Date and title required'); return; }
    try {
      await api.disputes.createChronologyEntry(matterId, { ...form, matter_id: matterId });
      toast.success('Entry added');
      setShowAdd(false);
      setForm({ date: '', end_date: '', title: '', description: '', category: 'factual', significance: 'medium', linked_evidence_ids: [], linked_issue_ids: [] });
      loadData();
    } catch { toast.error('Failed to add entry'); }
  };

  const handleDelete = async (id) => {
    try { await api.disputes.deleteChronologyEntry(id); toast.success('Entry removed'); loadData(); } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const filtered = filterCat ? entries.filter(e => e.category === filterCat) : entries;

  return (
    <div className="space-y-4" data-testid="chronology-builder">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Chronology ({entries.length} entries)</h3>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAdd(true)} data-testid="add-chronology-btn"><Plus className="h-3 w-3" /> Add Entry</Button>
      </div>

      {/* Category Filters */}
      <div className="flex flex-wrap gap-1.5">
        {CATEGORIES.map(cat => {
          const count = entries.filter(e => e.category === cat).length;
          if (count === 0 && filterCat !== cat) return null;
          return (
            <Badge key={cat} className={`text-[10px] cursor-pointer ${filterCat === cat ? 'ring-2 ring-slate-900' : ''} ${CAT_COLORS[cat]?.split(' ').slice(0, 2).join(' ') || 'bg-slate-100'}`} onClick={() => setFilterCat(filterCat === cat ? '' : cat)}>
              {cat} ({count})
            </Badge>
          );
        })}
        {filterCat && <Badge variant="outline" className="text-[10px] cursor-pointer" onClick={() => setFilterCat('')}>Clear</Badge>}
      </div>

      {/* Timeline */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><Clock className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No chronology entries yet</p></CardContent></Card>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-slate-200" />
          {filtered.map((e, i) => (
            <div key={e.id} className="relative mb-3" data-testid={`chrono-${e.id}`}>
              <div className={`absolute -left-4 top-3 w-3 h-3 rounded-full border-2 ${CAT_COLORS[e.category]?.split(' ')[2] || 'border-slate-300'} bg-white`} />
              <Card className="ml-2">
                <CardContent className="p-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono font-medium text-slate-600">{e.date}</span>
                        {e.end_date && <><ArrowRight className="h-3 w-3 text-slate-400" /><span className="text-xs font-mono text-slate-500">{e.end_date}</span></>}
                        <Badge className={`text-[9px] ${CAT_COLORS[e.category]?.split(' ').slice(0, 2).join(' ')}`}>{e.category}</Badge>
                        <Badge className={`text-[9px] ${SIG_COLORS[e.significance]}`}>{e.significance}</Badge>
                      </div>
                      <h4 className="text-sm font-medium text-slate-800">{e.title}</h4>
                      {e.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{e.description}</p>}
                      {e.linked_evidence_ids?.length > 0 && <span className="text-[10px] text-slate-400 mt-1 block">{e.linked_evidence_ids.length} linked evidence</span>}
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(e.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">Add Chronology Entry</DialogTitle>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Date *</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} data-testid="chrono-date-input" /></div>
              <div><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Payment certificate issued" data-testid="chrono-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({...f, category: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Significance</Label>
                <Select value={form.significance} onValueChange={v => setForm(f => ({...f, significance: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2} placeholder="What happened..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAdd} data-testid="submit-chronology"><Plus className="h-4 w-4 mr-1" /> Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
